import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import type { DBFreelancer, DBFreelancerDiscipline } from "@/lib/db-types";
import type { FreelancerDisciplineRef, FreelancerListRow } from "@/lib/freelancers";

/**
 * VIB-2925: frilanserbanken. Lista hentes med fetchAllPaginated, så den aldri
 * kuttes stille ved 1000 rader. Fagfelt og vurderingstall kommer som nøstede
 * rader og flates ut her, så komponentene får ferdige FreelancerListRow.
 */

const LIST_COLUMNS =
  "*, freelancer_discipline_links(is_primary, freelancer_disciplines(id, label, sort_order)), freelancer_reviews(rating)";

interface RawFreelancerRow extends DBFreelancer {
  freelancer_discipline_links: { is_primary: boolean; freelancer_disciplines: Omit<FreelancerDisciplineRef, "is_primary"> | null }[] | null;
  freelancer_reviews: { rating: number }[] | null;
}

export function toListRow(raw: RawFreelancerRow): FreelancerListRow {
  const { freelancer_discipline_links, freelancer_reviews, ...rest } = raw;
  const disciplines = (freelancer_discipline_links ?? [])
    .filter((l) => l.freelancer_disciplines)
    .map((l) => ({ ...l.freelancer_disciplines!, is_primary: l.is_primary }))
    .sort((a, b) => Number(b.is_primary) - Number(a.is_primary) || a.sort_order - b.sort_order);
  return {
    ...rest,
    links: Array.isArray(rest.links) ? rest.links : [],
    tags: rest.tags ?? [],
    disciplines,
    ratings: (freelancer_reviews ?? []).map((r) => r.rating),
  };
}

export function useFreelancers() {
  return useQuery({
    queryKey: ["freelancers"],
    queryFn: async (): Promise<FreelancerListRow[]> => {
      const rows = await fetchAllPaginated<RawFreelancerRow>("freelancers", LIST_COLUMNS, undefined, ["name", "id"]);
      return rows.map(toListRow);
    },
    staleTime: 60 * 1000,
  });
}

export function useFreelancer(id: string | undefined) {
  return useQuery({
    queryKey: ["freelancer-detail", id],
    enabled: !!id,
    queryFn: async (): Promise<FreelancerListRow | null> => {
      const { data, error } = await supabase.from("freelancers").select(LIST_COLUMNS).eq("id", id!).maybeSingle();
      if (error) throw error;
      return data ? toListRow(data as unknown as RawFreelancerRow) : null;
    },
  });
}

/** Alle fagfelt, også deaktiverte (admin trenger dem). Velgere filtrerer på is_active. */
export function useFreelancerDisciplines() {
  return useQuery({
    queryKey: ["freelancer-disciplines"],
    queryFn: async (): Promise<DBFreelancerDiscipline[]> => {
      const { data, error } = await supabase
        .from("freelancer_disciplines")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("label", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DBFreelancerDiscipline[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Innsyn (GDPR art. 15): alt Revehiet har om én frilanser som JSON. Filene
 * listes med navn; selve filene hentes fra profilen. Bare admin får knappen.
 */
export async function exportFreelancerData(id: string): Promise<Record<string, unknown>> {
  const [freelancer, reviews, comments, contacts] = await Promise.all([
    supabase.from("freelancers").select("*, freelancer_discipline_links(is_primary, freelancer_disciplines(label))").eq("id", id).single(),
    supabase.from("freelancer_reviews").select("rating, comment, created_at, project_id").eq("freelancer_id", id),
    supabase
      .from("freelancer_comments")
      .select("body, created_at, deleted_at, freelancer_comment_attachments(name, file_size)")
      .eq("freelancer_id", id),
    supabase.from("freelancer_contacts").select("name, role, role_note, email, phone, created_at").eq("freelancer_id", id),
  ]);
  for (const r of [freelancer, reviews, comments, contacts]) if (r.error) throw r.error;
  return {
    exported_at: new Date().toISOString(),
    freelancer: freelancer.data,
    reviews: reviews.data,
    comments: comments.data,
    contacts: contacts.data,
  };
}
